import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  Sheets,
} from '@ingka/modal';
import Button from '@ingka/button';
import Select, { Option } from '@ingka/select';
import Text from '@ingka/text';
import FormField from '@ingka/form-field';
import TextArea from '@ingka/text-area';
import Loading, { LoadingBall } from '@ingka/loading';
import { Box, Stack } from '@mui/material';
import {
  colourNeutral2,
  colourTextAndIcon1,
  colourTextAndIcon2,
  fontWeightBold,
} from '@ingka/variables';
import { RmppTotalModalProps } from './RmppTotalModalProps';
import SignedKeurInput from './SignedKeurInput';
import useTranslation from '../../hooks/useTranslation';
import { useTypedSelector } from '../../store';
import {
  fetchGlobalForecast,
  submitGlobalForecast,
} from '../../store/financialFrames/actionWrappers';
import {
  selectGlobalForecast,
  selectGlobalForecastLoading,
  selectGlobalForecastSubmitLoading,
} from '../../store/financialFrames/selectors';
import {
  FRAME_GROUPS,
  SUB_PORTFOLIO_BY_GROUP,
} from '../../store/financialFrames/stateType';
import { getForecastRows, getReportedValueRows, keurInputToEur } from './utils';
import ForecastTable from './ForecastTable';

const COMMENT_LIMIT = 200;

const RmppTotalModal: FC<RmppTotalModalProps> = ({
  isVisible,
  fiscalYear,
  initialGroup,
  onClose,
}) => {
  const dispatch = useDispatch();
  const t = useTranslation();
  const globalForecast = useTypedSelector(selectGlobalForecast);
  const loading = useTypedSelector(selectGlobalForecastLoading);
  const submitLoading = useTypedSelector(selectGlobalForecastSubmitLoading);

  const [group, setGroup] = useState<string>(FRAME_GROUPS[0].group);
  const [opex, setOpex] = useState('');
  const [capex, setCapex] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!isVisible || !fiscalYear) return;
    setGroup(initialGroup || FRAME_GROUPS[0].group);
    setOpex('');
    setCapex('');
    setComment('');
    dispatch(fetchGlobalForecast(fiscalYear));
  }, [isVisible, fiscalYear, initialGroup]);

  const subPortfolio = SUB_PORTFOLIO_BY_GROUP[group];
  const entry =
    globalForecast?.fiscalYear === fiscalYear
      ? globalForecast.subPortfolios.find(
          (item) => item.subPortfolio === subPortfolio,
        )
      : undefined;

  const submittedEntry = useRef(undefined);
  const wasSubmitting = useRef(false);
  useEffect(() => {
    if (wasSubmitting.current && !submitLoading) {
      if (entry !== submittedEntry.current) onClose();
    }
    wasSubmitting.current = submitLoading;
  }, [submitLoading]);

  const opexEur = keurInputToEur(opex);
  const capexEur = keurInputToEur(capex);
  const canSubmit =
    !!entry &&
    !!comment.trim() &&
    comment.length <= COMMENT_LIMIT &&
    (!!opexEur || !!capexEur);

  const onSubmit = () => {
    if (!canSubmit) return;
    submittedEntry.current = entry;
    dispatch(
      submitGlobalForecast({
        fiscalYear,
        subPortfolio,
        opex: opexEur,
        capex: capexEur,
        comment: comment.trim(),
      }),
    );
  };

  const renderReportedValue = () => {
    if (!entry?.latestAdjustment) return null;
    return (
      <Stack spacing={2}>
        <Text style={{ fontWeight: fontWeightBold }} color={colourTextAndIcon1}>
          {t('financialFrames.rmppTotal.reportedValue')}
        </Text>
        <Stack spacing={1}>
          <Text color={colourTextAndIcon2}>
            {t('financialFrames.rmppTotal.comment')}
          </Text>
          <Box
            sx={{
              backgroundColor: colourNeutral2,
              borderRadius: '4px',
              px: 1.5,
              py: 1.5,
            }}
          >
            <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {entry.latestAdjustment.comment}
            </Text>
          </Box>
        </Stack>
        <ForecastTable
          rows={getReportedValueRows(entry)}
          reportedLabel={t('financialFrames.rmppTotal.reportedRmppForecast')}
        />
      </Stack>
    );
  };

  return (
    <Modal
      handleCloseBtn={onClose}
      visible={isVisible}
      data-testid={'rmpp-total-modal'}
    >
      <Sheets
        alignment={'right'}
        size={'medium'}
        header={
          <ModalHeader
            floating={false}
            title={`${t('financialFrames.rmppTotal.title')} · ${fiscalYear ?? ''}`}
          />
        }
        footer={
          <ModalFooter>
            <Button
              fluid
              style={{ marginBottom: 0 }}
              text={t('common.cancel')}
              data-testid={'rmpp-total-cancel'}
              type="secondary"
              onClick={onClose}
              disabled={submitLoading}
            />
            <Button
              fluid
              text={t('financialFrames.rmppTotal.submitForecast')}
              data-testid={'rmpp-total-submit'}
              type="primary"
              onClick={onSubmit}
              loading={submitLoading}
              disabled={!canSubmit}
            />
          </ModalFooter>
        }
      >
        <ModalBody>
          <Stack spacing={3}>
            <Select
              id={'rmpp-total-sub-portfolio'}
              data-testid={'rmpp-total-sub-portfolio'}
              label={t('financialFrames.rmppTotal.subPortfolio')}
              value={group}
              hintText={''}
              onChange={(e) => setGroup(e.target.value)}
            >
              {FRAME_GROUPS.map((frameGroup) => (
                <Option
                  key={frameGroup.group}
                  value={frameGroup.group}
                  name={t(`financialFrames.columns.${frameGroup.group}`)}
                />
              ))}
            </Select>
            {loading ? (
              <Loading text={t('common.loadingMsg.loading')}>
                <LoadingBall />
              </Loading>
            ) : entry ? (
              <>
                <ForecastTable
                  rows={getForecastRows(entry)}
                  reportedLabel={t(
                    'financialFrames.rmppTotal.reportedForecast',
                  )}
                />
                <Stack spacing={1}>
                  <Text
                    style={{ fontWeight: fontWeightBold }}
                    color={colourTextAndIcon1}
                  >
                    {t('financialFrames.rmppTotal.globalForecast')}
                  </Text>
                  <Stack
                    direction={'row'}
                    gap={2}
                    sx={{ '.input-field': { width: '100%' } }}
                  >
                    <SignedKeurInput
                      id={'rmpp-total-opex-input'}
                      label={t('financialFrames.columns.opex')}
                      value={opex}
                      onChange={setOpex}
                    />
                    <SignedKeurInput
                      id={'rmpp-total-capex-input'}
                      label={t('financialFrames.columns.capex')}
                      value={capex}
                      onChange={setCapex}
                    />
                  </Stack>
                  <Text
                    tagName="span"
                    textStyle={'Body.S'}
                    style={{ color: colourTextAndIcon2 }}
                  >
                    {t('financialFrames.rmppTotal.globalForecastHelper')}
                  </Text>
                </Stack>
                <FormField
                  characterLimit={COMMENT_LIMIT}
                  fieldHelper={{
                    id: 'rmpp-total-comment-helper',
                    msg: t('financialFrames.rmppTotal.commentHelper'),
                    type: '',
                  }}
                >
                  <TextArea
                    id={'rmpp-total-comment'}
                    label={t('financialFrames.rmppTotal.comment')}
                    req
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </FormField>
                {renderReportedValue()}
              </>
            ) : (
              <Text>{t('financialFrames.rmppTotal.noData')}</Text>
            )}
          </Stack>
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

export default RmppTotalModal;
